import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Shared ProtectedRoute component for all Airclou apps.
 *
 * Wraps protected pages to ensure only authenticated users can access them.
 * Reads auth state from the shared AppAuthProvider and:
 *   1. Guards against bfcache restoring stale pages after idle logout
 *   2. Shows a loading spinner during auth bootstrap
 *   3. Redirects unauthenticated users to the Auth login page with a
 *      `redirect` parameter so they return to the same page after login
 */
import { useEffect } from 'react';
import { useAppAuth, isManualLogoutInProgress } from './AppAuthProvider';
import { getServiceUrl } from '@airclou/utils';
import { LoadingSpinner, useFlickerGuard, useRegisterLoading } from '@airclou/ui';
/**
 * Default full-screen centered loading spinner.
 */
function DefaultLoadingFallback() {
    return (_jsx("div", { className: "flex h-screen items-center justify-center", children: _jsx(LoadingSpinner, {}) }));
}
export function ProtectedRoute({ children, loadingFallback }) {
    const { appName, getToken, isAuthenticated, isLoading } = useAppAuth();
    // If a LoadingSkeletonProvider is in the tree, register this guard's loading
    // state with it and return null while loading — the provider renders the
    // singleton skeleton above. If no provider is mounted, fall back to the
    // local flicker-guarded `loadingFallback`.
    const hasLoadingProvider = useRegisterLoading(isLoading);
    const showLoading = useFlickerGuard(isLoading);
    // Guard against browser back-forward cache (bfcache) restoring a page
    // after the user was logged out due to inactivity. Firefox in particular
    // will serve the previous page from bfcache when the user presses Back,
    // bypassing a full page load and leaving stale React state in memory.
    useEffect(() => {
        const handlePageShow = (event) => {
            if (event.persisted) {
                const wasLoggedOut = sessionStorage.getItem(`${appName}:loggedOutInactivity`);
                const hasToken = !!getToken();
                if (wasLoggedOut || !hasToken) {
                    // Force a full reload so the auth provider re-evaluates from scratch.
                    window.location.reload();
                }
            }
        };
        window.addEventListener('pageshow', handlePageShow);
        return () => window.removeEventListener('pageshow', handlePageShow);
    }, [appName, getToken]);
    if (isLoading) {
        // Provider-mode: the singleton skeleton renders above us — render null.
        if (hasLoadingProvider)
            return null;
        // No-provider mode: legacy flicker-guarded local fallback.
        if (showLoading)
            return _jsx(_Fragment, { children: loadingFallback ?? _jsx(DefaultLoadingFallback, {}) });
        // Inside the delayed-show window — render nothing rather than falling
        // through to the unauthenticated redirect before auth resolves.
        return null;
    }
    if (!isAuthenticated) {
        // A manual sign-out owns navigation to the clean login URL. Don't race it
        // with a redirect that re-appends the current page as `?redirect=` — the
        // `setUser(null)` inside logout() re-renders us before the browser leaves.
        if (isManualLogoutInProgress())
            return null;
        const authServiceUrl = getServiceUrl('auth');
        const redirectUrl = encodeURIComponent(window.location.href);
        window.location.href = `${authServiceUrl}/login?app=${appName}&redirect=${redirectUrl}`;
        return null;
    }
    return _jsx(_Fragment, { children: children });
}
